// src/components/settings/TeamManagement.tsx
//
// Settings → Team (Story 1.4): the org admin's market-access user editor.
// Lists console users (market_access_user + admin) with position, active
// state and attribute chips; creates users via the admin-users invite flow;
// edits position + brand/region/channel/payer assignments; deactivates /
// reactivates. All data access goes through useTeam → teamService; the region
// picker reuses useRegions (the one region query path). Overlay/focus/error
// idioms follow RegionManagement (Story 1.1, reviewed and hardened).

import React, { useMemo, useRef, useState } from 'react'
import { Plus, UserCog, X } from 'lucide-react'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { useTeam } from '../../hooks/useTeam'
import { useRegions } from '../../hooks/useRegions'
import { useAuthStore } from '../../stores/authStore'
import { buildRegionTree, flattenRegionTree } from '../../utils/regionTree'
import {
  ATTRIBUTE_TYPES,
  attributesForUser,
  groupByType,
  chipLabel,
  positionsInUse,
  valuesInUse,
} from '../../utils/teamAttributes'
import type { YakTeamMember, YakUserAttributeType } from '../../types/yak'

const SELF_DEACTIVATE_COPY = "You can't deactivate your own account"
// The UX spine fixes this consequence copy verbatim.
const DEACTIVATE_CONSEQUENCE = 'their open tickets will be reassigned by rule'

function fullName(member: YakTeamMember): string {
  const name = [member.first_name, member.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  return name || member.email || 'Unnamed user'
}

function roleLabel(role: string | null): string {
  if (role === 'admin' || role === 'super_admin') return 'Admin'
  if (role === 'market_access_user') return 'Market access'
  return role ?? '—'
}

export function TeamManagement() {
  const {
    members,
    attributes,
    loading,
    error,
    createUser,
    updatePosition,
    addAttribute,
    removeAttribute,
    deactivate,
    reactivate,
  } = useTeam()
  const { regions } = useRegions()
  const { user } = useAuthStore()

  const [creating, setCreating] = useState(false)
  const [editMember, setEditMember] = useState<YakTeamMember | null>(null)
  const [deactivateTarget, setDeactivateTarget] =
    useState<YakTeamMember | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // create form
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    position: '',
  })
  // edit panel drafts
  const [draftPosition, setDraftPosition] = useState('')
  const [addType, setAddType] = useState<YakUserAttributeType>('brand')
  const [addValue, setAddValue] = useState('')
  const [addRegionId, setAddRegionId] = useState('')

  // Focus returns to the control that opened an overlay when it closes; falls
  // back to the section heading when that control unmounted (e.g. a row that
  // changed after a mutation) — WCAG 2.2 AA floor (RegionManagement pattern).
  const overlayTriggerRef = useRef<HTMLElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  const restoreFocus = (trigger: HTMLElement | null) => {
    if (trigger && document.contains(trigger)) trigger.focus()
    else headingRef.current?.focus()
  }

  const regionNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of regions) map.set(r.id, r.name)
    return map
  }, [regions])
  const regionRows = useMemo(
    () => flattenRegionTree(buildRegionTree(regions)),
    [regions]
  )
  const positionSuggestions = useMemo(() => positionsInUse(members), [members])
  const valueSuggestions = useMemo(
    () => (addType === 'region' ? [] : valuesInUse(attributes, addType)),
    [attributes, addType]
  )

  const runMutation = async (fn: () => Promise<void>, onDone?: () => void) => {
    setPending(true)
    setActionError(null)
    try {
      await fn()
      onDone?.()
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Something went wrong'
      )
    } finally {
      setPending(false)
    }
  }

  // --- create ---
  const openCreate = (e: React.MouseEvent<HTMLElement>) => {
    overlayTriggerRef.current = e.currentTarget
    setForm({ email: '', first_name: '', last_name: '', position: '' })
    setActionError(null)
    setSuccessMsg(null)
    setCreating(true)
  }
  const closeCreate = () => {
    const trigger = overlayTriggerRef.current
    overlayTriggerRef.current = null
    setCreating(false)
    restoreFocus(trigger)
  }
  const submitCreate = () => {
    if (pending) return
    const email = form.email.trim()
    const first = form.first_name.trim()
    const last = form.last_name.trim()
    if (!email || !first || !last) return
    runMutation(
      () =>
        createUser({
          email,
          first_name: first,
          last_name: last,
          position: form.position.trim() || null,
        }),
      () => {
        setSuccessMsg(
          `Invitation sent to ${email}. They appear below and can set their password from the invite email.`
        )
        closeCreate()
      }
    )
  }

  // --- edit (position + attributes) ---
  const openEdit = (
    e: React.MouseEvent<HTMLElement>,
    member: YakTeamMember
  ) => {
    overlayTriggerRef.current = e.currentTarget
    setEditMember(member)
    setDraftPosition(member.position ?? '')
    setAddType('brand')
    setAddValue('')
    setAddRegionId('')
    setActionError(null)
  }
  const closeEdit = () => {
    const trigger = overlayTriggerRef.current
    overlayTriggerRef.current = null
    setEditMember(null)
    restoreFocus(trigger)
  }
  const savePosition = () => {
    if (!editMember || pending) return
    runMutation(() =>
      updatePosition(editMember.id, draftPosition.trim() || null)
    )
  }
  const submitAddAttribute = () => {
    if (!editMember || pending) return
    if (addType === 'region') {
      if (!addRegionId) return
      runMutation(
        () => addAttribute(editMember.id, 'region', addRegionId),
        () => setAddRegionId('')
      )
    } else {
      const v = addValue.trim()
      if (!v) return
      runMutation(
        () => addAttribute(editMember.id, addType, v),
        () => setAddValue('')
      )
    }
  }
  const removeAttr = (attributeId: string) => {
    if (pending) return
    runMutation(() => removeAttribute(attributeId))
  }

  // --- deactivate / reactivate ---
  const openDeactivate = (
    e: React.MouseEvent<HTMLElement>,
    member: YakTeamMember
  ) => {
    overlayTriggerRef.current = e.currentTarget
    setActionError(null)
    setSuccessMsg(null)
    setDeactivateTarget(member)
  }
  const confirmDeactivate = () => {
    if (!deactivateTarget || pending) return
    // Stash the cascade count between the mutation and the onDone message
    // (runMutation doesn't surface fn's return value).
    let reassignedCount = 0
    runMutation(
      async () => {
        reassignedCount = await deactivate(deactivateTarget.id)
      },
      () => {
        const trigger = overlayTriggerRef.current
        overlayTriggerRef.current = null
        setDeactivateTarget(null)
        restoreFocus(trigger)
        // State the consequence delivered (Story 4.4 fulfils 1.4's promise).
        setSuccessMsg(
          reassignedCount > 0
            ? `Deactivated. ${reassignedCount} open ticket${
                reassignedCount === 1 ? ' was' : 's were'
              } reassigned by rule.`
            : 'Deactivated. They had no open tickets.'
        )
      }
    )
  }
  const onReactivate = (member: YakTeamMember) => {
    if (pending) return
    setSuccessMsg(null)
    runMutation(() => reactivate(member.id))
  }

  const positionsDatalist = (
    <datalist id="team-positions">
      {positionSuggestions.map(p => (
        <option key={p} value={p} />
      ))}
    </datalist>
  )

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Team</h2>
        <div className="space-y-3" aria-hidden="true">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="md:hidden mb-4 bg-gray-50 border border-gray-200 text-gray-600 rounded-lg px-4 py-3 text-sm">
        The Team editor works best on a larger screen.
      </div>

      <div className="flex justify-between items-center mb-4">
        <div>
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="text-xl font-bold text-gray-900 focus:outline-none"
          >
            Team
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Market-access users with a position and attribute assignments;
            routing rules resolve tickets to them.
          </p>
        </div>
        {members.length > 0 && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Add team member
          </Button>
        )}
      </div>

      {successMsg && (
        <div
          role="status"
          className="mb-4 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm"
        >
          {successMsg}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
        >
          {error}
        </div>
      )}
      {actionError && !creating && !editMember && !deactivateTarget && (
        <div
          role="alert"
          className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
        >
          {actionError}
        </div>
      )}

      {members.length === 0 ? (
        <div className="text-center py-10">
          <UserCog className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">
            Your organization has no market-access users yet.
          </p>
          <Button onClick={openCreate}>Add the first team member</Button>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {members.map(member => {
            const isSelf = member.id === user?.id
            const isActive = member.status === 'active'
            const userAttrs = attributesForUser(attributes, member.id)
            const groups = groupByType(userAttrs)
            return (
              <div
                key={member.id}
                className="group flex items-start justify-between py-3 gap-4"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">
                      {fullName(member)}
                    </span>
                    <Badge variant={isActive ? 'green' : 'gray'}>
                      {isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {roleLabel(member.role)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">{member.email}</div>
                  {member.position && (
                    <div className="text-sm text-gray-700 mt-0.5">
                      {member.position}
                    </div>
                  )}
                  {groups.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {groups.flatMap(group =>
                        group.items.map(a => (
                          <span
                            key={a.id}
                            className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs"
                          >
                            {chipLabel(
                              a,
                              a.region_id
                                ? regionNameById.get(a.region_id)
                                : undefined
                            )}
                          </span>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={e => openEdit(e, member)}
                  >
                    Edit
                  </Button>
                  {isActive ? (
                    isSelf ? (
                      <span className="text-xs text-gray-400 px-2">
                        {SELF_DEACTIVATE_COPY}
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={e => openDeactivate(e, member)}
                      >
                        Deactivate
                      </Button>
                    )
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onReactivate(member)}
                      loading={pending}
                    >
                      Reactivate
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {positionsDatalist}

      {/* Add team member */}
      {creating && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => {
            if (e.key === 'Escape') closeCreate()
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-member-title"
            className="bg-white rounded-lg shadow-lg max-w-md w-full p-6"
          >
            <h3
              id="add-member-title"
              className="text-lg font-semibold text-gray-900 mb-4"
            >
              Add team member
            </h3>
            {actionError && (
              <div
                id="add-member-error"
                role="alert"
                className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
              >
                {actionError}
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  autoFocus
                  type="email"
                  aria-label="Email"
                  aria-describedby={
                    actionError ? 'add-member-error' : undefined
                  }
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First name
                  </label>
                  <input
                    aria-label="First name"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                    value={form.first_name}
                    onChange={e =>
                      setForm({ ...form, first_name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last name
                  </label>
                  <input
                    aria-label="Last name"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                    value={form.last_name}
                    onChange={e =>
                      setForm({ ...form, last_name: e.target.value })
                    }
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  list="team-positions"
                  aria-label="Position"
                  placeholder="e.g. field reimbursement manager"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                  value={form.position}
                  onChange={e => setForm({ ...form, position: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={closeCreate} disabled={pending}>
                Cancel
              </Button>
              <Button
                onClick={submitCreate}
                loading={pending}
                disabled={
                  !form.email.trim() ||
                  !form.first_name.trim() ||
                  !form.last_name.trim()
                }
              >
                Send invitation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit member: position + attributes */}
      {editMember && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => {
            if (e.key === 'Escape') closeEdit()
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-member-title"
            className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-4">
              <h3
                id="edit-member-title"
                className="text-lg font-semibold text-gray-900"
              >
                {fullName(editMember)}
              </h3>
              <button
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600"
                onClick={closeEdit}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {actionError && (
              <div
                id="edit-member-error"
                role="alert"
                className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
              >
                {actionError}
              </div>
            )}

            {/* Position */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position
              </label>
              <div className="flex gap-2">
                <input
                  list="team-positions"
                  aria-label="Position"
                  placeholder="e.g. account director"
                  aria-describedby={
                    actionError ? 'edit-member-error' : undefined
                  }
                  className="block flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                  value={draftPosition}
                  onChange={e => setDraftPosition(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') savePosition()
                  }}
                />
                <Button
                  variant="outline"
                  onClick={savePosition}
                  loading={pending}
                >
                  Save
                </Button>
              </div>
            </div>

            {/* Current attributes */}
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Attribute assignments
              </div>
              {(() => {
                const userAttrs = attributesForUser(attributes, editMember.id)
                const groups = groupByType(userAttrs)
                if (groups.length === 0) {
                  return (
                    <p className="text-sm text-gray-500">
                      No attributes assigned yet.
                    </p>
                  )
                }
                return (
                  <div className="space-y-2">
                    {groups.map(group => (
                      <div key={group.type} className="flex flex-wrap gap-1.5">
                        {group.items.map(a => (
                          <span
                            key={a.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs"
                          >
                            {chipLabel(
                              a,
                              a.region_id
                                ? regionNameById.get(a.region_id)
                                : undefined
                            )}
                            <button
                              aria-label={`Remove ${chipLabel(
                                a,
                                a.region_id
                                  ? regionNameById.get(a.region_id)
                                  : undefined
                              )}`}
                              className="text-gray-400 hover:text-gray-700"
                              onClick={() => removeAttr(a.id)}
                              disabled={pending}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                )
              })()}
            </div>

            {/* Add attribute */}
            <div className="border-t border-gray-100 pt-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Add an assignment
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Type
                  </label>
                  <select
                    className="px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                    value={addType}
                    onChange={e => {
                      setAddType(e.target.value as YakUserAttributeType)
                      setAddValue('')
                      setAddRegionId('')
                    }}
                  >
                    {ATTRIBUTE_TYPES.map(t => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                {addType === 'region' ? (
                  <div className="flex-1 min-w-[12rem]">
                    <label className="block text-xs text-gray-500 mb-1">
                      Region
                    </label>
                    <select
                      aria-label="Region"
                      className="block w-full px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                      value={addRegionId}
                      onChange={e => setAddRegionId(e.target.value)}
                    >
                      <option value="">Select a region…</option>
                      {regionRows.map(({ region, depth }) => (
                        <option key={region.id} value={region.id}>
                          {`${'  '.repeat(depth)}${region.name}`}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="flex-1 min-w-[12rem]">
                    <label className="block text-xs text-gray-500 mb-1">
                      Value
                    </label>
                    <input
                      list="attr-values"
                      aria-label="Attribute value"
                      placeholder={`e.g. ${addType === 'brand' ? 'neffy' : addType === 'payer' ? 'Caremark' : 'commercial'}`}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                      value={addValue}
                      onChange={e => setAddValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') submitAddAttribute()
                      }}
                    />
                    <datalist id="attr-values">
                      {valueSuggestions.map(v => (
                        <option key={v} value={v} />
                      ))}
                    </datalist>
                  </div>
                )}
                <Button
                  variant="outline"
                  onClick={submitAddAttribute}
                  loading={pending}
                  disabled={
                    addType === 'region' ? !addRegionId : !addValue.trim()
                  }
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate confirmation */}
      {deactivateTarget && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => {
            if (e.key === 'Escape') {
              const trigger = overlayTriggerRef.current
              overlayTriggerRef.current = null
              setDeactivateTarget(null)
              restoreFocus(trigger)
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="deactivate-title"
            className="bg-white rounded-lg shadow-lg max-w-md w-full p-6"
          >
            <h3
              id="deactivate-title"
              className="text-lg font-semibold text-gray-900 mb-1"
            >
              Deactivate {fullName(deactivateTarget)}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              They can no longer sign in, and {DEACTIVATE_CONSEQUENCE}.
            </p>
            {actionError && (
              <div
                role="alert"
                className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
              >
                {actionError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                autoFocus
                variant="ghost"
                onClick={() => {
                  const trigger = overlayTriggerRef.current
                  overlayTriggerRef.current = null
                  setDeactivateTarget(null)
                  restoreFocus(trigger)
                }}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={confirmDeactivate}
                loading={pending}
              >
                Deactivate user
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
